import type { ModelInfo } from '@tabel/shared/connector/protocol';
import type { AgentId } from '@tabel/shared/connector/versions';
import { useQuery } from '@tanstack/react-query';
import { Check, ChevronDown, RefreshCw } from 'lucide-react';
import {
  type KeyboardEvent as ReactKeyboardEvent,
  useEffect,
  useId,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react';
import { agentModels, patchThread } from '../../lib/agent-api';
import { ApiError, reportClientError } from '../../lib/errors';
import { ErrorCodes, errorCode, errorSentence } from '../ErrorToast';
import { Button, Modal, Segmented, Skeleton, inputCls } from '../ui';
import { CARD_TEXTS } from './texts-cards';
import type { ModelPickerProps } from './types';

/*
 * Выбор модели и уровня рассуждения под полем сообщения (006, T112; US6-1; FR-037, FR-039; contracts/ui.md §2–§3, §6;
 * мокап Main.dc.html, PhoneModel.dc.html).
 * - Список моделей и уровней — от самого агента: `GET /api/agent/connectors/:id/agents/:agent/models`
 *   (`useAgentModels`, общий кэш: полоса состояний панели берёт из него «модели не загрузились»). Пока компьютер не
 *   ответил, сервер отдаёт запасной список (`fallback`): пометка «Список моделей не загрузился. Показан запасной
 *   список.» и «Загрузить список снова»; на кнопке — «· запасной список». Список старый (`stale`) — пометка тоже.
 * - Кнопка — модель и уровень: «Модель Б · среднее»; на телефоне — только модель. Модель не задана — модель агента по
 *   умолчанию.
 * - Выбор: модель — `listbox` (↑ ↓ Home End по пунктам, Enter и нажатие выбирают, выбор закрывает окно), уровень —
 *   сегменты (больше 3 уровней — список: длинные подписи в одну строку не помещаются); уровень окно не закрывает
 *   (дизайн-система, «Свойства и выбор значений»). Смена — со следующего ответа: есть разговор — `PATCH /api/agent/threads/:id` по одному запросу за раз,
 *   затем `onChange`; разговора ещё нет — сразу `onChange`. У новой модели нет текущего уровня — уровень модели по
 *   умолчанию. Ошибка — в окне, с кодом ошибки; на кнопке остаётся прежняя модель.
 * - Компьютер, планшет — окно над кнопкой (300 px); Esc закрывает только его (правая панель под ним Esc не получает),
 *   нажатие мимо — тоже; фокус — на текущей модели, после закрытия — на кнопке. Телефон (до 640 px) — нижний лист
 *   (`Modal`: на телефоне окно выезжает снизу), цели нажатия 40 px.
 */

const { label, text } = CARD_TEXTS;
const SCREEN = 'Assistant';

/** Ключ кэша списка моделей агента на компьютере. */
export const modelsKey = (connectorId: number, agent: AgentId) =>
  ['agent-models', connectorId, agent] as const;

/** Список моделей агента: тот же запрос и кэш у выбора модели и полосы состояний панели. */
export function useAgentModels(connectorId: number, agent: AgentId) {
  return useQuery({
    queryKey: modelsKey(connectorId, agent),
    queryFn: () => agentModels(connectorId, agent),
    staleTime: 60_000,
  });
}

/** Уровень рассуждения словами; незнакомый — как дал агент. */
export function effortLabel(effort: string): string {
  return Object.hasOwn(CARD_TEXTS.effort, effort) ? (CARD_TEXTS.effort[effort] as string) : effort;
}

/* Телефон — до 640 px (дизайн-система, ступень `bp-phone`): выпадающие списки — нижние листы (FR-039). */
const PHONE = '(max-width: 640px)';
function readPhone(): boolean {
  if (typeof window.matchMedia === 'function') return window.matchMedia(PHONE).matches;
  return window.innerWidth <= 640; // jsdom: matchMedia нет
}
function subscribePhone(onChange: () => void): () => void {
  window.addEventListener('resize', onChange);
  const mq = typeof window.matchMedia === 'function' ? window.matchMedia(PHONE) : null;
  mq?.addEventListener?.('change', onChange);
  return () => {
    window.removeEventListener('resize', onChange);
    mq?.removeEventListener?.('change', onChange);
  };
}
function usePhoneScreen(): boolean {
  return useSyncExternalStore(subscribePhone, readPhone, () => false);
}

/** Сбой связи, сервера или ответ не по договору — в серверный журнал (принцип II). */
const shouldReport = (e: unknown) =>
  !(e instanceof ApiError) || e.code === 'NETWORK' || e.code === 'INTERNAL' || e.status >= 500;

interface Choice {
  model: string;
  effort: string | null;
}

export function ModelPicker({
  connectorId,
  agent,
  model,
  effort,
  threadId,
  onChange,
  disabled,
}: ModelPickerProps) {
  const q = useAgentModels(connectorId, agent);
  const phone = usePhoneScreen();
  const [open, setOpen] = useState(false);
  /** Выбор, который уходит на сервер: на кнопке сразу он; ошибка — прежний. */
  const [shown, setShown] = useState<Choice | null>(null);
  const [saving, setSaving] = useState(false);
  const [failure, setFailure] = useState<unknown>(null);
  const chain = useRef<Promise<void>>(Promise.resolve());
  const wrap = useRef<HTMLDivElement>(null);
  const latest = useRef(onChange);
  latest.current = onChange;

  // Родитель принял новый выбор — показываем его.
  // biome-ignore lint/correctness/useExhaustiveDependencies: сброс — при смене модели или уровня у родителя
  useEffect(() => setShown(null), [model, effort]);

  const list = q.data?.models ?? [];
  const fallback = q.data?.fallback === true;
  const stale = q.data?.stale === true;
  const fallbackModel = list.find((m) => m.isDefault) ?? list[0];
  const curId = shown?.model ?? model ?? fallbackModel?.id ?? null;
  const cur = list.find((m) => m.id === curId);
  const curEffort = shown ? shown.effort : (effort ?? cur?.defaultEffort ?? null);
  const name = cur?.name ?? (model || label.defaultModel);
  const effortText = fallback ? label.fallbackShort : curEffort ? effortLabel(curEffort) : null;

  const trigger = () => wrap.current?.querySelector<HTMLElement>('button[aria-haspopup]') ?? null;
  const close = (focus = true) => {
    setOpen(false);
    if (focus) trigger()?.focus();
  };

  // Фокус — на текущей модели (и когда список пришёл позже). Нижний лист сначала открывает окно (`Modal`, эффект
  // дочернего компонента идёт раньше), браузер ставит фокус на «Закрыть» — здесь фокус переходит на модель.
  const ready = list.length > 0;
  useEffect(() => {
    if (!open || !ready) return;
    if ((document.activeElement as HTMLElement | null)?.getAttribute('role') === 'option') return;
    const root = wrap.current;
    (
      root?.querySelector<HTMLElement>('[role="option"][aria-selected="true"]') ??
      root?.querySelector<HTMLElement>('[role="option"]')
    )?.focus();
  }, [open, ready]);

  // Окно над кнопкой: нажатие мимо закрывает (нижний лист закрывает `Modal`).
  useEffect(() => {
    if (!open || phone) return;
    const onDown = (e: MouseEvent) => {
      if (!wrap.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open, phone]);

  /** Записать выбор: по одному запросу за раз, последний выбор — последним. `then` — после записи. */
  const save = (next: Choice, then?: () => void) => {
    setFailure(null);
    if (!threadId) {
      latest.current(next.model, next.effort);
      then?.();
      return;
    }
    setShown(next);
    setSaving(true);
    chain.current = chain.current.then(async () => {
      try {
        const t = await patchThread(threadId, { model: next.model, effort: next.effort });
        latest.current(t.model ?? next.model, t.effort);
        then?.();
      } catch (e) {
        setShown(null);
        setFailure(e);
        if (shouldReport(e)) reportClientError(SCREEN, 'change-model', e);
      } finally {
        setSaving(false);
      }
    });
  };

  const pickModel = (m: ModelInfo) => {
    const nextEffort =
      curEffort && m.efforts.includes(curEffort) ? curEffort : (m.defaultEffort ?? m.efforts[0] ?? null);
    if (m.id === curId && nextEffort === curEffort) {
      close();
      return;
    }
    save({ model: m.id, effort: nextEffort }, () => close());
  };
  const pickEffort = (e: string) => {
    if (!curId || e === curEffort) return;
    save({ model: curId, effort: e });
  };

  const onPopKey = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'Escape') return;
    // Только этот слой: правая панель под окном Esc не получает (событие помечено обработанным).
    e.preventDefault();
    e.stopPropagation();
    close();
  };

  const body = (
    <PickerBody
      phone={phone}
      loading={q.isPending}
      loadError={q.isError && !q.data ? q.error : null}
      reloading={q.isFetching}
      onReload={() => void q.refetch()}
      fallback={fallback}
      stale={stale && !fallback}
      list={list}
      curId={curId}
      cur={cur}
      curEffort={curEffort}
      onPickModel={pickModel}
      onPickEffort={pickEffort}
      saving={saving}
      failure={failure}
    />
  );

  return (
    <div ref={wrap} className="relative min-w-0">
      <Button
        variant="ghost"
        size="sm"
        aria-haspopup="dialog"
        aria-expanded={open}
        title={label.modelTitle}
        disabled={disabled}
        onClick={() => {
          setFailure(null);
          setOpen((v) => !v);
        }}
        className="max-w-full max-sm:min-h-10"
      >
        <span className="min-w-0 truncate">{name}</span>
        {effortText && <span className="max-sm:hidden">{` · ${effortText}`}</span>}
        <span className="sr-only">{` — ${label.modelTitle}`}</span>
        <ChevronDown size={16} strokeWidth={1.75} className="shrink-0" aria-hidden />
      </Button>
      {phone ? (
        <Modal
          open={open}
          onClose={() => close(false)}
          title={label.modelTitle}
          description={text.modelNote}
          width="w-[420px]"
        >
          {body}
        </Modal>
      ) : (
        open && (
          <div
            role="dialog"
            aria-label={label.modelTitle}
            onKeyDown={onPopKey}
            className="absolute bottom-full left-0 z-40 mb-2 w-[300px] max-w-[calc(100vw-32px)] overflow-hidden rounded-card border border-line bg-surface py-1 shadow-pop"
          >
            {body}
          </div>
        )
      )}
    </div>
  );
}

const GROUP_H = 'px-3 pt-1.5 pb-1 text-[11px] font-medium tracking-[0.04em] text-ink-3 uppercase';

function PickerBody({
  phone,
  loading,
  loadError,
  reloading,
  onReload,
  fallback,
  stale,
  list,
  curId,
  cur,
  curEffort,
  onPickModel,
  onPickEffort,
  saving,
  failure,
}: {
  phone: boolean;
  loading: boolean;
  loadError: unknown;
  reloading: boolean;
  onReload: () => void;
  fallback: boolean;
  stale: boolean;
  list: readonly ModelInfo[];
  curId: string | null;
  cur: ModelInfo | undefined;
  curEffort: string | null;
  onPickModel: (m: ModelInfo) => void;
  onPickEffort: (e: string) => void;
  saving: boolean;
  failure: unknown;
}) {
  const modelsId = useId();
  const reload = (
    <Button
      variant="ghost"
      size="sm"
      icon={RefreshCw}
      disabled={reloading}
      onClick={onReload}
      className="max-sm:min-h-10"
    >
      {label.reloadModels}
    </Button>
  );
  const pad = phone ? '-mx-3' : '';
  if (loading)
    return (
      <div aria-busy="true" className="space-y-2 px-3 py-2">
        <p className="text-[13px] text-ink-2">{label.loadingModels}</p>
        <Skeleton className="h-4 w-3/5" />
        <Skeleton className="h-4 w-2/5" />
      </div>
    );
  if (loadError !== null)
    return (
      <div className="space-y-2 px-3 py-2">
        <Problem title={text.modelsFailed} error={loadError} />
        {reload}
      </div>
    );
  const efforts = cur?.efforts ?? [];
  return (
    <div className={`flex flex-col ${pad}`}>
      {(fallback || stale) && (
        <div className="mx-3 mt-1 mb-1.5 flex flex-col items-start gap-1 rounded-control bg-subtle px-2.5 py-2 text-[12px] text-ink-2">
          <p>{fallback ? text.modelsFallback : text.modelsStale}</p>
          {reload}
        </div>
      )}
      <p id={modelsId} className={GROUP_H}>
        {label.model}
      </p>
      <ModelList labelId={modelsId} list={list} curId={curId} disabled={saving} onPick={onPickModel} />
      {efforts.length > 0 && (
        <>
          <hr className="my-1 border-line" />
          <p className={GROUP_H}>{label.effort}</p>
          <div className="px-2 pt-0.5 pb-1.5">
            {/* Пять уровней Claude в одну строку окна 300 px не помещаются (стенд 18.10): больше трёх — списком. */}
            {efforts.length <= 3 ? (
              <Segmented
                label={label.effort}
                value={curEffort ?? ''}
                onChange={onPickEffort}
                options={efforts.map((e) => ({ value: e, label: effortLabel(e), disabled: saving }))}
                className="max-sm:flex max-sm:w-full"
              />
            ) : (
              <EffortList efforts={efforts} cur={curEffort} disabled={saving} onPick={onPickEffort} />
            )}
          </div>
        </>
      )}
      {failure !== null && (
        <div className="px-3 py-1.5">
          <Problem title={text.modelNotChanged} error={failure} />
        </div>
      )}
      {!phone && (
        <>
          <hr className="my-1 border-line" />
          <p className="px-3 pt-1 pb-1.5 text-[12px] text-ink-2">{text.modelNote}</p>
        </>
      )}
    </div>
  );
}

/** ↑ ↓ Home End — по пунктам списка. */
function moveFocus(e: ReactKeyboardEvent<HTMLElement>) {
  const items = [...e.currentTarget.querySelectorAll<HTMLElement>('[role="option"]:not(:disabled)')];
  const i = items.indexOf(document.activeElement as HTMLElement);
  const n = items.length;
  const to: Record<string, number | undefined> = {
    ArrowDown: (i + 1) % n,
    ArrowUp: (i - 1 + n) % n,
    Home: 0,
    End: n - 1,
  };
  const k = to[e.key];
  if (k === undefined || n === 0) return;
  e.preventDefault();
  items[k]?.focus();
}

const OPTION =
  'flex w-full items-start gap-2.5 px-3 py-1.5 text-left text-[13.5px] text-ink transition-colors hover:bg-subtle focus:bg-subtle focus:outline-none disabled:cursor-not-allowed disabled:text-ink-3 max-sm:min-h-10';

/** С этого числа моделей над списком — поле поиска, список прокручивается (pi с OpenRouter — сотни моделей). */
export const MODEL_SEARCH_FROM = 13;

function ModelList({
  labelId,
  list,
  curId,
  disabled,
  onPick,
}: {
  labelId: string;
  list: readonly ModelInfo[];
  curId: string | null;
  disabled: boolean;
  onPick: (m: ModelInfo) => void;
}) {
  const [query, setQuery] = useState('');
  const long = list.length >= MODEL_SEARCH_FROM;
  const words = query.trim().toLocaleLowerCase('ru').split(/\s+/u).filter(Boolean);
  // Поиск — по названию, поставщику и идентификатору, все слова запроса; текущая модель видна всегда.
  const shown =
    words.length === 0
      ? list
      : list.filter((m) => {
          if (m.id === curId) return true;
          const hay = `${m.name} ${m.provider ?? ''} ${m.id}`.toLocaleLowerCase('ru');
          return words.every((w) => hay.includes(w));
        });
  const nothing = words.length > 0 && !shown.some((m) => m.id !== curId);
  return (
    <>
      {long && (
        <div className="px-3 pb-1.5">
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            aria-label={label.findModel}
            placeholder={label.findModel}
            className={`${inputCls} max-sm:text-base`}
          />
        </div>
      )}
      <div
        role="listbox"
        tabIndex={-1}
        aria-labelledby={labelId}
        onKeyDown={moveFocus}
        className={`flex flex-col outline-none ${long ? 'max-h-[min(320px,40vh)] overflow-y-auto' : ''}`}
      >
        {shown.map((m) => {
          const on = m.id === curId;
          const sub = m.isDefault ? label.isDefault : (m.provider ?? null);
          return (
            <button
              key={m.id}
              type="button"
              role="option"
              aria-selected={on}
              tabIndex={on ? 0 : -1}
              data-autofocus={on ? '' : undefined}
              disabled={disabled}
              onClick={() => onPick(m)}
              className={OPTION}
            >
              <span className="min-w-0 flex-1 leading-[1.35]">
                <span className="block break-words">{m.name}</span>
                {sub && <span className="mt-0.5 block text-[12px] text-ink-2">{sub}</span>}
              </span>
              {on && <Check size={16} strokeWidth={1.75} className="mt-px shrink-0 text-ink" aria-hidden />}
            </button>
          );
        })}
      </div>
      {nothing && (
        <p role="status" className="px-3 py-1.5 text-[13px] text-ink-2">
          {text.modelNotFound}
        </p>
      )}
    </>
  );
}

/** Больше 3 уровней (Claude — 5, pi — 6) — список, а не сегменты: длинные подписи в окно 300 px не помещаются. */
function EffortList({
  efforts,
  cur,
  disabled,
  onPick,
}: {
  efforts: readonly string[];
  cur: string | null;
  disabled: boolean;
  onPick: (e: string) => void;
}) {
  return (
    <div
      role="listbox"
      tabIndex={-1}
      aria-label={label.effort}
      onKeyDown={moveFocus}
      className="-mx-2 flex flex-col outline-none"
    >
      {efforts.map((e) => {
        const on = e === cur;
        return (
          <button
            key={e}
            type="button"
            role="option"
            aria-selected={on}
            tabIndex={on ? 0 : -1}
            disabled={disabled}
            onClick={() => onPick(e)}
            className={OPTION}
          >
            <span className="min-w-0 flex-1">{effortLabel(e)}</span>
            {on && <Check size={16} strokeWidth={1.75} className="mt-px shrink-0 text-ink" aria-hidden />}
          </button>
        );
      })}
    </div>
  );
}

function Problem({ title, error }: { title: string; error: unknown }) {
  const requestId = error instanceof ApiError ? error.requestId : null;
  const code = errorCode(error);
  return (
    <div role="alert" className="rounded-control bg-bad-soft px-3 py-2 text-[13px] text-bad">
      <p className="font-semibold">{title}</p>
      <p>{errorSentence(error)}</p>
      {(requestId || code) && (
        <p className="mt-0.5 text-[12px]">
          {label.errorCode} <ErrorCodes requestId={requestId} code={code} />
        </p>
      )}
    </div>
  );
}
